import React, { useEffect, useRef, useState } from 'react';
import { Cast, Eye, X } from 'lucide-react';
import type { DynamicMemory, RGB, ZoneId } from '../../types/ambilight';
import { LightNodesOverlay } from '../LightNodes/LightNodesOverlay';
import { AmbientWallCanvas } from '../AmbientWebGL/AmbientWallCanvas';
import { captureEngine } from '../../services/captureEngine';
import {
  analyzeZoneDetailed,
  createDynamicMemory,
  getZoneRect,
  rgbToHex,
  ZONE_INFO_MAP,
} from '../../services/colorEngine';
import { useAmbilightContext } from '../../context/AmbilightContext';

export const VirtualScreen: React.FC = () => {
  const {
    points,
    bulbs,
    isCapturing,
    showZones,
    selectedPointId,
    selectPoint,
    toggleShareScreen,
    isZonePreviewOpen,
    toggleZonePreview,
    setIsZonePreviewOpen,
    inspectorZone,
    setInspectorZone,
    algorithm,
    tune,
    updatePointBorderWidth,
    getBulbNamesForPoint,
  } = useAmbilightContext();

  const videoRef = useRef<HTMLVideoElement>(null);
  const studioCanvasRef = useRef<HTMLCanvasElement>(null);
  const monitorRef = useRef<HTMLDivElement>(null);
  const thumbCanvasRef = useRef<HTMLCanvasElement>(null);

  const [currentReadout, setCurrentReadout] = useState<string>('Analizando zona...');
  const [currentZoneColor, setCurrentZoneColor] = useState<RGB>({ r: 255, g: 255, b: 255 });
  const [currentZoneBright, setCurrentZoneBright] = useState<number>(50);
  const dynMemRef = useRef<DynamicMemory>(createDynamicMemory());

  // Conectar video stream si está capturando
  useEffect(() => {
    if (isCapturing && videoRef.current) {
      videoRef.current.srcObject = captureEngine.getStream();
      videoRef.current.play().catch(() => {});
    }
  }, [isCapturing]);

  // Dibujar pantalla en reposo estática
  useEffect(() => {
    const dest = studioCanvasRef.current;
    if (!dest) return;
    const ctx = dest.getContext('2d');
    if (!ctx) return;

    const source = captureEngine.getStudioCanvas();
    if (dest.width !== source.width || dest.height !== source.height) {
      dest.width = source.width;
      dest.height = source.height;
    }
    ctx.drawImage(source, 0, 0);
  }, [isCapturing]);

  // Bucle de renderizado para "Lo que ve el foco" en tiempo real con throttling LQ (15 FPS)
  useEffect(() => {
    if (!isZonePreviewOpen) return;

    let animId: number;
    let lastRenderTime = 0;
    const minInterval = 66; // ~15 FPS: elimina el consumo de CPU por getImageData continuo

    const render = (now: number) => {
      if (now - lastRenderTime >= minInterval) {
        lastRenderTime = now;
        const tCanvas = thumbCanvasRef.current;
        if (tCanvas) {
          const tCtx = tCanvas.getContext('2d');
          if (tCtx) {
            const isCapturingNow = captureEngine.getIsCapturing();
            const video = captureEngine.getVideoElement();
            const studioCanvas = captureEngine.getStudioCanvas();

            let src: CanvasImageSource;
            let vw = 640;
            let vh = 360;

            if (isCapturingNow && video.videoWidth > 0) {
              src = video;
              vw = video.videoWidth;
              vh = video.videoHeight;
            } else {
              src = studioCanvas;
              vw = studioCanvas.width;
              vh = studioCanvas.height;
            }

            const point = points.find((p) => p.id === inspectorZone);
            const borderWidth = point ? point.borderWidth : 80;
            const rect = getZoneRect(vw, vh, inspectorZone, borderWidth);

            tCtx.drawImage(src, rect.x1, rect.y1, rect.w, rect.h, 0, 0, tCanvas.width, tCanvas.height);

            try {
              const imgData = tCtx.getImageData(0, 0, tCanvas.width, tCanvas.height);
              const detailed = analyzeZoneDetailed(
                imgData.data,
                tCanvas.width,
                tCanvas.height,
                algorithm,
                tune,
                dynMemRef.current
              );
              setCurrentReadout(detailed.info);
              setCurrentZoneColor(detailed.rawColor);
              setCurrentZoneBright(detailed.brightness);
            } catch {}
          }
        }
      }

      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animId);
  }, [isZonePreviewOpen, inspectorZone, algorithm, tune, points]);

  const getPoint = (id: ZoneId) => points.find((p) => p.id === id);
  const activePoint = points.find((p) => p.id === inspectorZone);
  const zoneInfo = ZONE_INFO_MAP[inspectorZone] || { name: inspectorZone, icon: '📍', short: '•' };
  const hexColor = rgbToHex(currentZoneColor.r, currentZoneColor.g, currentZoneColor.b);

  // Ancho efectivo escalado de cada punto de muestreo
  const bwTL = Math.max(20, (getPoint('top-left')?.borderWidth ?? 80) * 0.7);
  const bwTR = Math.max(20, (getPoint('top-right')?.borderWidth ?? 80) * 0.7);
  const bwBR = Math.max(20, (getPoint('bottom-right')?.borderWidth ?? 80) * 0.7);
  const bwBL = Math.max(20, (getPoint('bottom-left')?.borderWidth ?? 80) * 0.7);
  const bwT = Math.max(20, (getPoint('top')?.borderWidth ?? 80) * 0.7);
  const bwR = Math.max(20, (getPoint('right')?.borderWidth ?? 80) * 0.7);
  const bwB = Math.max(20, (getPoint('bottom')?.borderWidth ?? 80) * 0.7);
  const bwL = Math.max(20, (getPoint('left')?.borderWidth ?? 80) * 0.7);

  // Calcula posición del recorte visual en la pantalla según la zona activa (SIN solapamientos)
  const getActiveZoneStyle = (zone: ZoneId): React.CSSProperties => {
    switch (zone) {
      case 'top-left':
        return { top: 0, left: 0, width: `${bwTL}px`, height: `${bwTL}px` };
      case 'top':
        return { top: 0, left: `${bwTL}px`, right: `${bwTR}px`, height: `${bwT}px` };
      case 'top-right':
        return { top: 0, right: 0, width: `${bwTR}px`, height: `${bwTR}px` };
      case 'right':
        return { right: 0, top: `${bwTR}px`, bottom: `${bwBR}px`, width: `${bwR}px` };
      case 'bottom-right':
        return { bottom: 0, right: 0, width: `${bwBR}px`, height: `${bwBR}px` };
      case 'bottom':
        return { bottom: 0, left: `${bwBL}px`, right: `${bwBR}px`, height: `${bwB}px` };
      case 'bottom-left':
        return { bottom: 0, left: 0, width: `${bwBL}px`, height: `${bwBL}px` };
      case 'left':
        return { left: 0, top: `${bwTL}px`, bottom: `${bwBL}px`, width: `${bwL}px` };
      case 'center':
        return { top: '25%', bottom: '25%', left: '25%', right: '25%' };
      case 'all':
      default:
        return { inset: 0 };
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center p-4 sm:p-8 w-full max-w-5xl mx-auto select-none">
      {/* Wrapper del Monitor y su Pared de Luz WebGL */}
      <div className="relative w-full aspect-video flex items-center justify-center">
        {/* 0. Canvas WebGL de Iluminación Ambiental Fusionada en la Pared */}
        <div className="pointer-events-none absolute -inset-14 sm:-inset-24 md:-inset-36 lg:-inset-48 z-0 overflow-visible">
          <AmbientWallCanvas points={points} screenRectRef={monitorRef} />
        </div>

        {/* Marco del Monitor de Estudio (Pantalla dentro de la pantalla, plana) */}
        <div
          ref={monitorRef}
          className="relative z-10 w-full h-full rounded-2xl p-3 bg-gradient-to-b from-slate-800 via-slate-900 to-black border border-white/15 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)]"
          style={{
            boxShadow:
              '0 0 50px rgba(0,0,0,0.85), 0 20px 80px rgba(15,23,42,0.6), inset 0 1px 1px rgba(255,255,255,0.2)',
          }}
        >
          {/* Notch superior con sensor de luz ambiental y estado LQ */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-0.5 rounded-full bg-black/80 border border-white/10 text-[9px] text-white/50 z-20 shadow-md">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="tracking-widest uppercase font-semibold">WiZ SENSOR</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span className="font-mono text-emerald-400 flex items-center gap-0.5">
              <span>⚡</span> PREVIEW LQ
            </span>
          </div>

          {/* Superficie Interna de Pantalla (LA VISTA ACTUAL) */}
          <div className="relative w-full h-full rounded-xl overflow-hidden bg-black flex items-center justify-center border border-white/10 group">
            {/* 1. Video en vivo cuando se comparte pantalla */}
            {isCapturing ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              /* 2. Pantalla Standby Limpia */
              <canvas ref={studioCanvasRef} className="w-full h-full object-cover" />
            )}

            {/* Reflejo de cristal suave */}
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-white/[0.05]"
              style={{ mixBlendMode: 'screen' }}
            />

            {/* 3. MODO VISTA PREVIA DIRECTAMENTE EN LA VISTA ACTUAL */}
            {isZonePreviewOpen && (
              <>
                {/* Cuadrícula táctil para elegir zona haciendo clic en la pantalla actual */}
                <div className="absolute inset-0 z-20 grid grid-cols-3 grid-rows-3 cursor-crosshair">
                  <div onClick={() => setInspectorZone('top-left')} title="Superior Izquierda" />
                  <div onClick={() => setInspectorZone('top')} title="Superior Centro" />
                  <div onClick={() => setInspectorZone('top-right')} title="Superior Derecha" />
                  <div onClick={() => setInspectorZone('left')} title="Lado Izquierdo" />
                  <div onClick={() => setInspectorZone('all')} title="Toda la Pantalla (ALL)" />
                  <div onClick={() => setInspectorZone('right')} title="Lado Derecho" />
                  <div onClick={() => setInspectorZone('bottom-left')} title="Inferior Izquierda" />
                  <div onClick={() => setInspectorZone('bottom')} title="Inferior Centro" />
                  <div onClick={() => setInspectorZone('bottom-right')} title="Inferior Derecha" />
                </div>

                {/* Recuadro de la Zona Activa: atenúa todo el resto de la pantalla y resalta la zona */}
                <div
                  className="pointer-events-none absolute z-10 border-2 border-purple-400 bg-purple-500/10 transition-all duration-150 rounded"
                  style={{
                    ...getActiveZoneStyle(inspectorZone),
                    boxShadow:
                      '0 0 0 9999px rgba(0, 0, 0, 0.65), 0 0 20px rgba(168, 85, 247, 0.7)',
                  }}
                >
                  <div className="absolute top-1 left-1 px-2 py-0.5 rounded bg-purple-950/90 text-[10px] font-mono font-bold text-purple-200 border border-purple-400/40 whitespace-nowrap shadow-lg">
                    {zoneInfo.name} ({activePoint ? activePoint.borderWidth : 80}px)
                  </div>
                </div>
              </>
            )}

            {/* 4. VISUALIZACIÓN DE BORDES Y ESQUINAS CLICKEABLES (Modo Zonas) */}
            {showZones && !isZonePreviewOpen && (
              <div className="absolute inset-0 z-20 font-mono text-[9px] pointer-events-auto">
                {/* 1. Esquina Sup. Izq */}
                <div
                  onClick={() => selectPoint('top-left')}
                  className={`absolute border-2 border-dashed p-1.5 flex flex-col items-start justify-start cursor-pointer transition-all ${
                    selectedPointId === 'top-left'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ top: 0, left: 0, width: `${bwTL}px`, height: `${bwTL}px` }}
                  title="Esquina Superior Izquierda — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    TL ({getPoint('top-left')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('top-left')}
                  </span>
                </div>

                {/* 2. Borde Superior: Inicia en bwTL y termina en bwTR (cero solapamiento) */}
                <div
                  onClick={() => selectPoint('top')}
                  className={`absolute border-b-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                    selectedPointId === 'top'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ top: 0, left: `${bwTL}px`, right: `${bwTR}px`, height: `${bwT}px` }}
                  title="Borde Superior — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    TOP ({getPoint('top')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1.5 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-[80%]">
                    💡 {getBulbNamesForPoint('top')}
                  </span>
                </div>

                {/* 3. Esquina Sup. Der */}
                <div
                  onClick={() => selectPoint('top-right')}
                  className={`absolute border-2 border-dashed p-1.5 flex flex-col items-end justify-start cursor-pointer transition-all ${
                    selectedPointId === 'top-right'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ top: 0, right: 0, width: `${bwTR}px`, height: `${bwTR}px` }}
                  title="Esquina Superior Derecha — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    TR ({getPoint('top-right')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('top-right')}
                  </span>
                </div>

                {/* 4. Borde Derecho: Inicia en bwTR y termina en bwBR (cero solapamiento) */}
                <div
                  onClick={() => selectPoint('right')}
                  className={`absolute border-l-2 border-dashed flex flex-col items-end justify-center pr-1.5 cursor-pointer transition-all ${
                    selectedPointId === 'right'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ right: 0, top: `${bwTR}px`, bottom: `${bwBR}px`, width: `${bwR}px` }}
                  title="Borde Derecho — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    R ({getPoint('right')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('right')}
                  </span>
                </div>

                {/* 5. Esquina Inf. Der */}
                <div
                  onClick={() => selectPoint('bottom-right')}
                  className={`absolute border-2 border-dashed p-1.5 flex flex-col items-end justify-end cursor-pointer transition-all ${
                    selectedPointId === 'bottom-right'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ bottom: 0, right: 0, width: `${bwBR}px`, height: `${bwBR}px` }}
                  title="Esquina Inferior Derecha — Haz clic para configurar luz"
                >
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mb-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('bottom-right')}
                  </span>
                  <span className="text-cyan-200 font-bold drop-shadow">
                    BR ({getPoint('bottom-right')?.borderWidth}px)
                  </span>
                </div>

                {/* 6. Borde Inferior: Inicia en bwBL y termina en bwBR (cero solapamiento) */}
                <div
                  onClick={() => selectPoint('bottom')}
                  className={`absolute border-t-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                    selectedPointId === 'bottom'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ bottom: 0, left: `${bwBL}px`, right: `${bwBR}px`, height: `${bwB}px` }}
                  title="Borde Inferior — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    BOTTOM ({getPoint('bottom')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1.5 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-[80%]">
                    💡 {getBulbNamesForPoint('bottom')}
                  </span>
                </div>

                {/* 7. Esquina Inf. Izq */}
                <div
                  onClick={() => selectPoint('bottom-left')}
                  className={`absolute border-2 border-dashed p-1.5 flex flex-col items-start justify-end cursor-pointer transition-all ${
                    selectedPointId === 'bottom-left'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ bottom: 0, left: 0, width: `${bwBL}px`, height: `${bwBL}px` }}
                  title="Esquina Inferior Izquierda — Haz clic para configurar luz"
                >
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mb-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('bottom-left')}
                  </span>
                  <span className="text-cyan-200 font-bold drop-shadow">
                    BL ({getPoint('bottom-left')?.borderWidth}px)
                  </span>
                </div>

                {/* 8. Borde Izquierdo: Inicia en bwTL y termina en bwBL (cero solapamiento) */}
                <div
                  onClick={() => selectPoint('left')}
                  className={`absolute border-r-2 border-dashed flex flex-col items-start justify-center pl-1.5 cursor-pointer transition-all ${
                    selectedPointId === 'left'
                      ? 'border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/50'
                      : 'border-cyan-400 bg-cyan-400/15 hover:bg-cyan-400/25 hover:border-cyan-300'
                  }`}
                  style={{ left: 0, top: `${bwTL}px`, bottom: `${bwBL}px`, width: `${bwL}px` }}
                  title="Borde Izquierdo — Haz clic para configurar luz"
                >
                  <span className="text-cyan-200 font-bold drop-shadow">
                    L ({getPoint('left')?.borderWidth}px)
                  </span>
                  <span className="text-amber-300 font-semibold text-[9px] bg-black/75 px-1 py-0.5 rounded mt-0.5 border border-amber-400/30 truncate max-w-full">
                    💡 {getBulbNamesForPoint('left')}
                  </span>
                </div>
              </div>
            )}

            {/* 5. ZONAS TÁCTILES PERIMETRALES (Invisibles pero interactivas cuando showZones está apagado) */}
            {!showZones && !isZonePreviewOpen && (
              <div className="absolute inset-0 z-15 pointer-events-auto">
                {/* 4 Esquinas Táctiles */}
                <div
                  onClick={() => selectPoint('top-left')}
                  className="absolute top-0 left-0 w-[22%] h-[26%] cursor-pointer hover:bg-white/[0.04] transition-colors rounded-tl-xl"
                  title="Esquina Superior Izquierda — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('top-right')}
                  className="absolute top-0 right-0 w-[22%] h-[26%] cursor-pointer hover:bg-white/[0.04] transition-colors rounded-tr-xl"
                  title="Esquina Superior Derecha — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('bottom-left')}
                  className="absolute bottom-0 left-0 w-[22%] h-[26%] cursor-pointer hover:bg-white/[0.04] transition-colors rounded-bl-xl"
                  title="Esquina Inferior Izquierda — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('bottom-right')}
                  className="absolute bottom-0 right-0 w-[22%] h-[26%] cursor-pointer hover:bg-white/[0.04] transition-colors rounded-br-xl"
                  title="Esquina Inferior Derecha — Haz clic para ver y asignar luz"
                />

                {/* 4 Bordes Táctiles */}
                <div
                  onClick={() => selectPoint('top')}
                  className="absolute top-0 left-[22%] right-[22%] h-[16%] cursor-pointer hover:bg-white/[0.04] transition-colors"
                  title="Borde Superior — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('bottom')}
                  className="absolute bottom-0 left-[22%] right-[22%] h-[16%] cursor-pointer hover:bg-white/[0.04] transition-colors"
                  title="Borde Inferior — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('left')}
                  className="absolute left-0 top-[26%] bottom-[26%] w-[15%] cursor-pointer hover:bg-white/[0.04] transition-colors"
                  title="Borde Izquierdo — Haz clic para ver y asignar luz"
                />
                <div
                  onClick={() => selectPoint('right')}
                  className="absolute right-0 top-[26%] bottom-[26%] w-[15%] cursor-pointer hover:bg-white/[0.04] transition-colors"
                  title="Borde Derecho — Haz clic para ver y asignar luz"
                />
              </div>
            )}

            {/* Botón flotante central de compartir cuando está en reposo */}
            {!isCapturing && !isZonePreviewOpen && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-[3px] z-10 p-6 text-center pointer-events-none">
                <button
                  onClick={toggleShareScreen}
                  className="px-8 py-3.5 rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white font-bold text-sm shadow-xl shadow-purple-600/40 border border-white/20 transition-all hover:scale-105 active:scale-95 flex items-center gap-2.5 cursor-pointer pointer-events-auto"
                >
                  <Cast className="w-5 h-5 text-white" />
                  <span>COMPARTIR PANTALLA O VENTANA</span>
                </button>
                <p className="mt-2.5 text-xs text-white/70 max-w-sm leading-relaxed pointer-events-auto">
                  Selecciona tu pantalla, juego o ventana para iluminar tus luces WiZ al instante
                </p>
              </div>
            )}
          </div>

          {/* 8 Nodos de Luz con sus Resplandores (Ubicados en esquinas y centros) */}
          <LightNodesOverlay />
        </div>
      </div>

      {/* 6. DOCK INSPECTOR DE VISTA PREVIA */}
      {isZonePreviewOpen ? (
        <div className="mt-4 w-full rounded-2xl bg-slate-900/95 border border-purple-500/40 p-3.5 shadow-2xl shadow-purple-950/50 backdrop-blur-xl z-30 flex flex-wrap items-center justify-between gap-3 text-white transition-all">
          {/* A. Thumbnail "Lo que ve el foco" */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <canvas
                ref={thumbCanvasRef}
                width={96}
                height={54}
                className="rounded border border-purple-400/50 bg-black shadow-md block"
                style={{ imageRendering: 'pixelated' }}
              />
              <span className="absolute bottom-0 right-0 px-1 py-0.2 rounded-tl text-[8px] font-mono bg-black/85 text-cyan-300">
                96×54
              </span>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-xs text-white/90 font-medium">
                <span>🔍 Lo que ve el foco:</span>
                <span className="text-purple-300 font-bold">{zoneInfo.name}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="w-3.5 h-3.5 rounded-full border border-white/40 shadow-sm"
                  style={{ backgroundColor: hexColor }}
                />
                <span className="font-mono text-xs font-bold text-white">{hexColor}</span>
                <span className="text-[11px] text-white/60">({currentZoneBright}%)</span>
              </div>
            </div>
          </div>

          {/* B. Análisis del Algoritmo */}
          <div className="hidden lg:flex flex-col max-w-xs text-[11px] text-purple-200/90 font-mono bg-purple-950/40 border border-purple-500/20 px-3 py-1.5 rounded-xl">
            <span className="text-[9px] text-purple-400 font-bold uppercase">
              Algoritmo: {algorithm}
            </span>
            <span className="truncate">{currentReadout}</span>
          </div>

          {/* C. Slider de Ancho de Borde para esta zona */}
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-white/70 whitespace-nowrap">Ancho:</span>
            <input
              type="range"
              min={10}
              max={350}
              step={5}
              value={activePoint ? activePoint.borderWidth : 80}
              onChange={(e) => {
                if (activePoint) {
                  updatePointBorderWidth(activePoint.id, Number(e.target.value));
                }
              }}
              className="w-24 sm:w-28 h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
            />
            <span className="font-mono text-xs font-bold text-cyan-300 w-12 text-right">
              {activePoint ? activePoint.borderWidth : 80}px
            </span>
          </div>

          {/* D. Selector 3x3 compacto + Botón Cerrar */}
          <div className="flex items-center gap-2">
            <div className="grid grid-cols-3 gap-0.5 p-1 bg-black/50 rounded-lg border border-white/10">
              {(
                [
                  'top-left',
                  'top',
                  'top-right',
                  'left',
                  'all',
                  'right',
                  'bottom-left',
                  'bottom',
                  'bottom-right',
                ] as ZoneId[]
              ).map((zid) => (
                <button
                  key={zid}
                  onClick={() => setInspectorZone(zid)}
                  className={`w-5 h-5 rounded text-[9px] font-mono flex items-center justify-center transition-all cursor-pointer ${
                    inspectorZone === zid
                      ? 'bg-purple-600 text-white font-bold'
                      : 'text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                  title={ZONE_INFO_MAP[zid]?.name || zid}
                >
                  {ZONE_INFO_MAP[zid]?.short || '•'}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsZonePreviewOpen(false)}
              className="px-3 py-1.5 rounded-xl bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 text-white text-xs font-semibold transition-colors flex items-center gap-1 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
              <span>Cerrar</span>
            </button>
          </div>
        </div>
      ) : (
        /* Botón toggle del inspector de vista sobre la imagen actual */
        <div className="mt-4 flex items-center justify-center z-30">
          <button
            onClick={toggleZonePreview}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600/20 hover:bg-purple-600/35 border border-purple-500/40 text-purple-200 text-xs font-semibold cursor-pointer shadow-lg backdrop-blur-md transition-all active:scale-95"
            title="Abrir inspector de vista sobre la imagen actual"
          >
            <Eye className="w-4 h-4 text-purple-400" />
            <span>Abrir Inspector de Vista</span>
          </button>
        </div>
      )}
    </div>
  );
};
